import 'primary/events/components/matchs/edit/ModalEditScore.scss';

import { Alert, Box, Divider, TextField } from '@mui/material';
import { IMatchRepository } from 'domain/event/match/Match.repository';
import { UnpyMatch } from 'domain/event/match/UnpyMatch';
import { PlayerTeam } from 'domain/event/PlayerTeam';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { TypeEventCodeEnum } from 'domain/event/TypeEventCodeEnum';
import { setMatchUpdateModalClose } from 'domain/modal/store/ModalSlice';
import { formatDate } from 'helper/date.helper';
import { InputScore } from 'primary/classements/commons/MatchEditInputs';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { NeonText } from 'primary/Components/NeonText/NeonText';
import { TagPlayer } from 'primary/Components/TagPlayer/TagPlayer';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import ModalContainer from 'primary/ModalContainer/ModalContainer';
import React, { FC } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import * as yup from 'yup';

export interface ModaEditScoreProps {
  isOpen: boolean;
  handleClose: () => void;
  systemPoint: SystemPointsEnum;
  onValidEdit: () => void;
  match?: UnpyMatch;
}

type ScoreUpdateValidationFormData = {
  idMatchParticipant: number;
  score?: number;
  result?: string;
};

export type MatchUpdateFormData = {
  id: number;
  startDate?: string;
  scoreUpdateValidations: ScoreUpdateValidationFormData[];
};

const validationSchema = (t: UseTranslateReturn) =>
  yup.object({
    id: yup.number().required(t('general.forms.errors.required')),
    startDate: yup.string().nullable(),
    scoreUpdateValidations: yup
      .array()
      .of(
        yup.object({
          idMatchParticipant: yup.number().nullable(),
          score: yup.number().nullable(),
          result: yup.string().nullable(),
        }),
      )
      .nullable(),
  });

const useOnSubmit =
  (
    matchRepository: IMatchRepository,
    onSubmitted: () => void,
    setLoadingState: (loadingState: boolean) => void,
  ) =>
  (data: MatchUpdateFormData) => {
    setLoadingState(true);
    matchRepository
      .update({
        ...data,
        scoreUpdateValidations: data.scoreUpdateValidations.filter(
          (s) => !!s.idMatchParticipant,
        ),
      })
      .then(() => {
        onSubmitted();
        sendEventToastMessage('event.matchs.edit.success', 'success');
      })
      .finally(() => {
        setLoadingState(false);
      });
  };

export const ModalEditScoreMatchWrapper = () => {
  const stateModal = useSelector((state: RootState) => state.modal.matchUpdate);

  if (stateModal?.isOpen) {
    //@ts-ignore
    return <ModalEditScoreMatch {...stateModal.props} isOpen={stateModal.isOpen} />;
  } else {
    return null;
  }
};

export const ModalEditScoreMatch: FC<ModaEditScoreProps> = (props) => {
  const { matchsRepository } = useContextDependency();
  const { systemPoint, onValidEdit, match } = props as ModaEditScoreProps;
  const resolver = useYupValidationResolver(validationSchema);
  const dispatch = useDispatch();
  const handleClose = () => dispatch(setMatchUpdateModalClose());
  const participant1 = match?.participants[0];
  const participant2 = match?.participants[1];
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    register,
    getValues,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<MatchUpdateFormData>({
    resolver,
    defaultValues: {
      id: match?.id,
      startDate: match?.startDate
        ? formatDate(match?.startDate, 'inputDatetime')
        : undefined,
      scoreUpdateValidations: match?.participants.map((p) => ({
        idMatchParticipant: p.id,
        score: systemPoint === SystemPointsEnum.PERSO ? p.score : undefined,
        result: systemPoint === SystemPointsEnum.DEFAULT ? p.result : undefined,
      })),
    },
  });
  const [loading, setLoading] = React.useState(false);
  const onSubmit = useOnSubmit(
    matchsRepository,
    () => {
      onValidEdit();
    },
    setLoading,
  );
  const nameScore1 =
    systemPoint === SystemPointsEnum.DEFAULT
      ? 'scoreUpdateValidations.0.result'
      : 'scoreUpdateValidations.0.score';
  const nameScore2 =
    systemPoint === SystemPointsEnum.DEFAULT
      ? 'scoreUpdateValidations.1.result'
      : 'scoreUpdateValidations.1.score';
  const t = useTranslate();
  const score1Control = useController({
    control: control,
    name: nameScore1,
  });
  const score2Control = useController({
    control: control,
    name: nameScore2,
  });

  return (
    <ModalContainer
      handleClose={handleClose}
      isOpen={props.isOpen}
      header={{
        title: t('event.matchs.edit.modal.title'),
      }}
      content={
        <div className={'modal-match-edit'}>
          <form onSubmit={handleSubmit(onSubmit)} className={'-formContainer'}>
            <div className={'-fieldContainer'}>
              <Box className={'-editScores'}>
                {(!participant1 || !participant2) && (
                  <Alert severity="info">
                    {t(`event.matchs.edit.modal.noticeNoParticipant`)}
                  </Alert>
                )}
                {participant1 && participant2 && (
                  <>
                    <Alert severity="info">
                      {t(`event.matchs.edit.editScore.modal.notice.${systemPoint}`)}
                    </Alert>
                    <div className={'containerEditScore'}>
                      <div className={'-scoreEditItem'}>
                        <h4>
                          {t(`event.matchs.edit.editScore.modal.teamTitle`, {
                            teamName: participant1?.eventParticipant.team.name,
                          })}
                        </h4>
                        <InputScore
                          typeEvent={
                            participant1?.eventParticipant?.event?.typeEvent?.code
                          }
                          withoutNull={
                            match.group.event.typeEvent.code ===
                              TypeEventCodeEnum.TOURNAMENT ||
                            match.group.event.typeEvent.code ===
                              TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM
                          }
                          controlProps={score1Control}
                          setValue={setValue}
                          controlOtherProps={score2Control}
                          systemPoint={systemPoint}
                          errors={errors}
                        />

                        <Divider
                          orientation={'vertical'}
                          className={'-divider'}
                          flexItem
                        />

                        {participant1?.eventParticipant.team.players.map(
                          (player: PlayerTeam) => (
                            <TagPlayer
                              key={player?.player?.idPlayer}
                              player={player.player}
                            />
                          ),
                        )}
                      </div>
                      <div className={'-versusWithDate'}>
                        <NeonText variant={'orange'} text={'VS'} />
                        <TextField
                          variant={'standard'}
                          className={'-textField -filterText'}
                          type={'datetime-local'}
                          {...register('startDate')}
                          onChange={(event) => {
                            setValue('startDate', event.target.value);
                          }}
                        />
                        <span className={'-optionnalNotice'}>Optionnel</span>
                      </div>
                      <div className={'-scoreEditItem'}>
                        <h4>
                          {t(`event.matchs.edit.editScore.modal.teamTitle`, {
                            teamName: participant2?.eventParticipant.team.name,
                          })}
                        </h4>
                        <InputScore
                          typeEvent={
                            participant1?.eventParticipant?.event?.typeEvent?.code
                          }
                          setValue={setValue}
                          withoutNull={
                            match.group.event.typeEvent.code ===
                              TypeEventCodeEnum.TOURNAMENT ||
                            match.group.event.typeEvent.code ===
                              TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM
                          }
                          controlProps={score2Control}
                          controlOtherProps={score1Control}
                          systemPoint={systemPoint}
                          errors={errors}
                        />
                        {participant2?.eventParticipant.team.players.map(
                          (player: PlayerTeam) => (
                            <TagPlayer
                              key={player?.player?.idPlayer}
                              player={player.player}
                            />
                          ),
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Box>
            </div>
            <CoupleButtonValidCancel
              isTypeSubmit
              loading={loading}
              onClickCancel={handleClose}
            />
          </form>
        </div>
      }
    />
  );
};
